// React is imported automatically with JSX
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { useEffect, useState, useCallback, useRef } from 'react';
import { supabase } from './lib/supabase';
import AuthForm from './components/AuthForm';
import EmailVerification from './components/EmailVerification';
import ResetPassword from './pages/ResetPassword';
import ResetPasswordCallback from './pages/ResetPasswordCallback';
import SaveAsWebappModal from './components/SaveAsWebappModal';
import Navigation from './components/Navigation';
import NotificationIcon from './components/NotificationIcon';
import Community from './pages/Community';
import { MessagePermalink } from './components/community/MessagePermalink';
import Classroom from './pages/Classroom';
import ClassDetails from './pages/ClassDetails';
import Blueprints from './pages/Blueprints';
import LiveEvents from './pages/LiveEventsUser';
import AITool from './pages/AITool';
import RagAgent from './pages/RagAgent';
import Users from './pages/Users';
import Webhooks from './pages/Webhooks';
import Settings from './pages/Settings';
import Subscription from './pages/Subscription';
import AdminNotifications from './pages/AdminNotifications';
import AdminDashboard from './pages/AdminDashboard';
import { VideoModal } from './components/common/VideoModal';
import AdminLiveEvents from './pages/AdminLiveEvents';
import LiveEventBanner from './components/common/LiveEventBanner';

type User = {
  id: string;
  email?: string;
} | null;

function IntroModalController({ user, setShow }: { user: User; setShow: (v: boolean) => void }) {
  const location = useLocation();
  const timerRef = useRef<number | null>(null);

  useEffect(() => {
    if (!user) {
      if (timerRef.current) {
        window.clearTimeout(timerRef.current);
        timerRef.current = null;
      }
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const { data, error } = await supabase
          .from('user_preferences')
          .select('hide_loom_intro')
          .eq('user_id', (user as any).id)
          .maybeSingle();
        if (error) {
          console.warn('Intro pref fetch error:', error);
        }
        const hide = data?.hide_loom_intro === true;
        if (!hide && !cancelled) {
          timerRef.current = window.setTimeout(() => {
            setShow(true);
          }, 10000); // 10s after route load
        }
      } catch (e) {
        console.warn('Intro pref fetch failed:', e);
        timerRef.current = window.setTimeout(() => {
          setShow(true);
        }, 10000);
      }
    })();

    return () => {
      cancelled = true;
      if (timerRef.current) {
        window.clearTimeout(timerRef.current);
        timerRef.current = null;
      }
    };
  }, [user, location.key]);

  return null;
}

function AuthedShell({ user, needsSubscription }: { user: User; needsSubscription: boolean }) {
  const location = useLocation();
  // Hide global header on class details pages
  const isClassDetails = /^\/classroom\/[A-Za-z0-9-]+$/.test(location.pathname);

  return (
    <div className="flex w-full max-w-full">
      <Navigation />
      <main className="flex-1 w-full text-white px-0">
        {!isClassDetails && (
          <div className="fixed top-4 right-6 z-40">
            <NotificationIcon />
          </div>
        )}
        {/* Global live event banner (hidden on class details for mobile UX) */}
        {!isClassDetails && <LiveEventBanner />}
        <div className="w-full max-w-[1800px] mx-auto px-2 md:pl-24 md:pr-24">
          <Routes>
            {/* Subscription route is always accessible */}
            <Route path="/subscription" element={<Subscription />} />
            {/* Reset password callback needs to be accessible when user has a session */}
            <Route path="/reset-password-callback" element={<ResetPasswordCallback />} />
            {/* All other routes are protected and redirect to subscription if needed */}
            {needsSubscription ? (
              /* If user needs subscription, redirect all other routes to subscription */
              <Route path="*" element={<Navigate to="/subscription" replace />} />
            ) : (
              /* Regular routes for subscribed users */
              <>
                <Route path="/community" element={<Community />} />
                <Route path="/community/message/:messageId" element={<MessagePermalink />} />
                <Route path="/classroom" element={<Classroom />} />
                <Route path="/classroom/:classId" element={<ClassDetails />} />
                <Route path="/blueprints/*" element={<Blueprints />} />
                <Route path="/live-events" element={<LiveEvents />} />
                <Route path="/ai-tool" element={<AITool />} />
                <Route path="/agent" element={<RagAgent />} />
                {(user as any)?.email === 'markofilipovic2003@gmail.com' && (
                  <>
                    <Route path="/users" element={<Users />} />
                    <Route path="/webhooks" element={<Webhooks />} />
                    <Route path="/notifications" element={<AdminNotifications />} />
                    <Route path="/admin/dashboard" element={<AdminDashboard />} />
                    <Route path="/admin/live-events" element={<AdminLiveEvents />} />
                  </>
                )}
                <Route path="/settings" element={<Settings />} />
                <Route path="*" element={<Navigate to="/agent" replace />} />
              </>
            )}
          </Routes>
        </div>
      </main>
    </div>
  );
}

function App() {
  const [user, setUser] = useState<User>(null);
  const [loading, setLoading] = useState(true);
  // Removed unused appVersion state
  const [showWebappModal, setShowWebappModal] = useState(false);
  const [needsSubscription, setNeedsSubscription] = useState(false);
  const [subscriptionChecked, setSubscriptionChecked] = useState(false);
  const initialLoadRef = useRef(true);
  // Global Loom intro modal state
  const [showIntroModal, setShowIntroModal] = useState(false);

  const checkIfMobile = () => {
    return /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
  };

  const checkVersion = async () => {
    try {
      const { data, error } = await supabase
        .from('app_version')
        .select('version')
        .order('created_at', { ascending: false })
        .limit(1)
        .maybeSingle();

      if (error && error.code !== 'PGRST116') throw error;

      const storedVersion = localStorage.getItem('appVersion');
      const newVersion = data?.version;

      if (newVersion && storedVersion !== newVersion) {
        // Clear cache and reload if version mismatch
        localStorage.setItem('appVersion', newVersion);
        if (!initialLoadRef.current) {
          window.location.reload();
        }
        // Version tracking is now handled via localStorage only
      }
    } catch (err) {
      console.error('Error checking version:', err);
    }
    initialLoadRef.current = false;
  };

  const handleAuthStateChange = useCallback((_event: string, session: any) => {
    const newUser = session?.user ?? null;
    setUser(newUser);
    
    if (newUser && checkIfMobile()) {
      // Check if we've shown the modal before
      const hasShownModal = localStorage.getItem('hasShownWebappModal');
      if (!hasShownModal) {
        setShowWebappModal(true);
      }
    }
  }, []);

  // Timer is now handled per-route by IntroModalController

  const handleCloseIntro = () => setShowIntroModal(false);
  const handleDontShowIntroAgain = async () => {
    try {
      if (!user) return setShowIntroModal(false);
      const { error } = await supabase
        .from('user_preferences')
        .upsert({ user_id: (user as any).id, hide_loom_intro: true }, { onConflict: 'user_id' });
      if (error) console.error('Error saving intro pref:', error);
    } finally {
      setShowIntroModal(false);
    }
  };

  // Check if the user needs a subscription
  const checkSubscriptionStatus = useCallback(async (userId: string) => {
    if (!userId) return;
    
    try {
      // Get user profile
      const { data: profile, error } = await supabase
        .from('profiles')
        .select('created_at, is_subscribed')
        .eq('id', userId)
        .single();
        
      if (error) throw error;
      
      // Check if user signed up today or later (after May 22, 2025)
      // And check if they are not subscribed
      if (profile) {
        const createdAt = profile.created_at ? new Date(profile.created_at) : null;
        const cutoffDate = new Date('2025-05-23T15:10:54+05:00'); // Current timestamp
        
        // If user signed up today or later and is not subscribed, they need to subscribe
        if (createdAt && createdAt >= cutoffDate && profile.is_subscribed === false) {
          setNeedsSubscription(true);
        } else {
          setNeedsSubscription(false);
        }
      }
    } catch (err) {
      console.error('Error checking subscription status:', err);
    } finally {
      setSubscriptionChecked(true);
    }
  }, []);

  useEffect(() => {
    // Check current session
    supabase.auth.getSession().then(({ data: { session } }) => {
      handleAuthStateChange('SIGNED_IN', session);
      if (session?.user) {
        checkSubscriptionStatus(session.user.id);
      }
      checkVersion();
      setLoading(false);
    });

    // Listen for auth changes
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((event, session) => {
      handleAuthStateChange(event, session);
      if (session?.user) {
        checkSubscriptionStatus(session.user.id);
      } else {
        setSubscriptionChecked(true);
      }
    });

    return () => subscription.unsubscribe();
  }, [handleAuthStateChange, checkSubscriptionStatus]);

  // Poll for version changes
  useEffect(() => {
    const interval = setInterval(checkVersion, 60000); // Check every minute
    return () => clearInterval(interval);
  }, []);
  const handleCloseWebappModal = () => {
    setShowWebappModal(false);
    localStorage.setItem('hasShownWebappModal', 'true');
  };

  if (loading || (user && !subscriptionChecked)) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-white">Loading...</div>
      </div>
    );
  }



  return (
    <div className="min-h-screen">
      {!user ? (
        <BrowserRouter>
          <Routes>
            <Route path="/verify" element={<EmailVerification />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="/reset-password-callback" element={<ResetPasswordCallback />} />
            <Route path="*" element={
              <div className="min-h-screen flex items-center justify-center px-1">
                <AuthForm />
              </div>
            } />
          </Routes>
        </BrowserRouter>
      ) : (
        <BrowserRouter>
          <AuthedShell user={user} needsSubscription={needsSubscription} />
          {/* Route-aware controller to re-arm the 15s intro timer on each navigation */}
          <IntroModalController user={user} setShow={setShowIntroModal} />
        </BrowserRouter>
      )}
      {showWebappModal && <SaveAsWebappModal onClose={handleCloseWebappModal} />}
      {/* Global Loom Intro Modal for authenticated users (excluded on auth pages since those render only when !user) */}
      {user && (
        <VideoModal
          open={showIntroModal}
          onClose={handleCloseIntro}
          onDontShowAgain={handleDontShowIntroAgain}
          title="Welcome! Quick tour"
          loomUrl="https://www.loom.com/share/761bb7720a8a4bedbce203ca4142ed88?sid=9daba95e-15d2-418a-8404-2b855b6aab31"
        />
      )}
    </div>
  );
}

export default App;
